// Simplified API client for secure import system
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export async function apiClient(endpoint: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem("auth_token");
  
  if (!token && process.env.NODE_ENV === 'development') {
    const tempToken = "dev-temp-token";
    localStorage.setItem("auth_token", tempToken);
  }

  const currentToken = localStorage.getItem("auth_token");
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  const defaultHeaders: Record<string, string> = {
    ...(currentToken && { 'Authorization': `Bearer ${currentToken}` }),
  };

  // Only add Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const fetchOptions: RequestInit = {
    method: 'GET',
    credentials: 'include',
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  };

  // Bug reale 19/08: fetch() senza AbortController puo' restare sospeso per
  // sempre se la connessione si interrompe senza risposta HTTP (es. il
  // server viene riavviato a meta' richiesta durante un deploy). Ogni UI che
  // disabilita i controlli "in attesa" di questa chiamata resta allora
  // bloccata per sempre, senza modo di uscirne se non ricaricando la pagina
  // (visto dal vivo sul modale di debrief meeting). Timeout qui protegge
  // ogni chiamante di questo client, non solo quello osservato.
  const REQUEST_TIMEOUT_MS = 30_000;
  const timeoutController = new AbortController();
  let timedOut = false;
  let abortedByCaller = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    timeoutController.abort();
  }, REQUEST_TIMEOUT_MS);
  const abortForCaller = () => {
    abortedByCaller = true;
    timeoutController.abort();
  };
  if (options.signal?.aborted) {
    abortForCaller();
  } else {
    options.signal?.addEventListener('abort', abortForCaller, { once: true });
  }

  try {
    const response = await fetch(url, { ...fetchOptions, signal: timeoutController.signal });

    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      const rawBody = await response.text().catch(() => '');
      let payload: any = undefined;
      if (rawBody && contentType.includes('application/json')) {
        try { payload = JSON.parse(rawBody); } catch { payload = undefined; }
      }
      const serverMessage = typeof payload?.message === 'string' ? payload.message : undefined;
      const code = typeof payload?.code === 'string' ? payload.code : undefined;
      const fallback = `HTTP error! status: ${response.status} endpoint: ${url} method: ${fetchOptions.method || 'GET'}`;
      throw new ApiRequestError(serverMessage || fallback, response.status, code, (payload ?? rawBody) || undefined);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return await response.text();
  } catch (rawError) {
    // Un'operazione resa obsoleta dal chiamante (React Query, unmount o nuova
    // ricerca) è un esito previsto: non deve apparire come timeout né sporcare
    // la console globale. Solo il nostro timer è un timeout reale.
    if (abortedByCaller && !timedOut) throw rawError;
    const error = timedOut && rawError instanceof DOMException && rawError.name === 'AbortError'
      ? new ApiRequestError('La richiesta non ha ricevuto risposta in tempo. Riprova.', 0, 'TIMEOUT')
      : rawError;
    console.error('API request error:', error);
    throw error;
  } finally {
    clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', abortForCaller);
  }
}
